import { Component, OnInit, Inject, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AutomationProvider } from 'src/providers/automation/automation';
import { Snackbar } from 'src/util/snackbar';
import { ConfirmAutomationChangesComponent } from '../confirm-automation-changes/confirm-automation-changes.component';
import { AutomationAction, AutomationActionNumber } from 'src/util/automations';

export interface AutomationReference {
  automationId: number;
  groupId: number;
  isPresent: boolean;
  isActive: boolean;
  description: string;
  automationType: string;
  automationTypeFormatted: string;
}

export interface DialogData {
  automations: AutomationReference[];
  selectedAutomationName: string;
}

@Component({
  selector: 'app-automacoes-empresa',
  templateUrl: './automacoes-empresa.component.html',
  styleUrls: ['./automacoes-empresa.component.scss']
})
export class AutomacoesEmpresaComponent implements OnInit {
  loading = false;
  gridAutomations: AutomationReference[] = [];
  selectedAutomations: AutomationReference[] = [];
  filterControl = new FormControl('');
  filteredAutomations: AutomationReference[] = [];
  public companyAutomations: AutomationReference[] = []
  public currentCompany: string = '';

  constructor(
    public modalService: NgbModal,
    public activeModal: NgbActiveModal,
    public snackBar: Snackbar,
    public automationProvider: AutomationProvider
  ) { }

  ngOnInit(): void {
    this.loadGridAutomations();
    this.setupFilter();
  }

  loadGridAutomations(): void {
    this.loading = true;
    this.companyAutomations = this.gridAutomations = [];
    const companyId = localStorage.getItem('idEmpresa');
    this.currentCompany = localStorage.getItem('idEmpresa') + " - " + localStorage.getItem('empresa');
    this.automationProvider.getAutomationsByCompany('fiscal', companyId)
      .then((result: any) => {
        this.companyAutomations = (result || []).map((companyAutomation) => {
          const [
            groupId,
            hasAutomation,
            automationId,
            description,
            isActive,
            automationTypeNumber
          ] = companyAutomation;

          const automationType = AutomationActionNumber[automationTypeNumber];

          return {
            groupId,
            automationId: hasAutomation ? automationId : null,
            description,
            isActive: hasAutomation ? isActive : false,
            automationType,
            automationTypeFormatted: AutomationAction[automationType],
            isPresent: Boolean(hasAutomation),
          };
        });

        this.gridAutomations = [...this.companyAutomations];

        this.selectedAutomations = this.gridAutomations
          .filter(a => a.isPresent)
          .map(a => ({ ...a }));
        this.filteredAutomations = [...this.gridAutomations];


      })
      .catch((error) => {
        this.snackBar.openLong(error.error.status + ' - ' + error.error.detail, 'erro')
      })
      .finally(() => {
        this.loading = false;
      });

  }

  setupFilter(): void {
    this.filterControl.valueChanges.subscribe(value => {
      this.applyFilter(value || '');
    });
  }

  applyFilter(filterValue: string): void {
    const filter = filterValue.toLowerCase().trim();
    if (!filter) {
      this.filteredAutomations = [...this.gridAutomations];
    } else {
      this.filteredAutomations = this.gridAutomations.filter(automation =>
        automation.description.toLowerCase().includes(filter) ||
        automation.groupId.toString().includes(filter)
      );
    }
  }

  isSelected(automation: AutomationReference): boolean {
    return this.selectedAutomations.some(a => a.groupId === automation.groupId);
  }

  toggleSelection(automation: AutomationReference): void {
    const index = this.selectedAutomations.findIndex(a => a.groupId === automation.groupId);
    if (index > -1) {
      this.selectedAutomations.splice(index, 1);
    } else {
      this.selectedAutomations.push({ ...automation, isPresent: true });
    }
  }

  getActiveState(automation: AutomationReference): boolean {
    const selected = this.selectedAutomations.find(a => a.groupId === automation.groupId);
    return selected ? selected.isActive : automation.isActive;
  }

  toggleActive(automation: AutomationReference): void {
    const selected = this.selectedAutomations.find(a => a.groupId === automation.groupId);
    if (selected) {
      selected.isActive = !selected.isActive;
    }
  }

  toggleAll(): void {
    if (this.getSelectionCount() === this.gridAutomations.length) {
      this.selectedAutomations = [];
    } else {
      this.selectedAutomations = [...this.gridAutomations];
    }
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }

  getAutomationChanges(): { newlyAdded: AutomationReference[], newlyRemoved: AutomationReference[] } {
    const newlyAdded = this.selectedAutomations.filter(automation => {
      const original = this.companyAutomations.find(a => a.groupId === automation.groupId);
      return original && !original.isPresent;
    });

    const newlyRemoved = this.companyAutomations.filter(automation => {
      const isSelected = this.selectedAutomations.some(a => a.groupId === automation.groupId);
      return automation.isPresent && !isSelected;
    });

    return { newlyAdded, newlyRemoved };
  }

  onConfirm(): Promise<void> {
    const { newlyAdded, newlyRemoved } = this.getAutomationChanges();

    if (newlyAdded.length === 0 && newlyRemoved.length === 0) {
      this.snackBar.open('Nenhuma alteração foi feita', 'warning');
      return;
    }

    const modalRef = this.modalService.open(ConfirmAutomationChangesComponent, {
      size: 'lg',
      backdrop: 'static',
      windowClass: localStorage.getItem('theme') == 'dark' ? 'dark-theme' : ''
    });

    modalRef.componentInstance.newlyAdded = newlyAdded;
    modalRef.componentInstance.newlyRemoved = newlyRemoved;

    modalRef.result.then((result) => {
      if (result) {
        this.snackBar.openLong('Automações editadas com sucesso!', 'success');
        this.loadGridAutomations();
        this.setupFilter();
      }
    })
  }

  getSelectionCount(): number {
    return this.selectedAutomations.length;
  }
}